import { AspChartOfAccount, Box, Button, RailzServiceNames, Select, SubTitle, Title } from 'vopay';
import { useTranslation } from 'react-i18next';
import AccountingSoftwareLayout from '../AccountingSoftwareLayout/AccountingSoftwareLayout';
import styles from './ChartOfAccounts.module.scss';
import { AccountingImageMap } from './AccountingImageMap';

type ChartOfAccountsProps = {
  onSave: () => void;
  isSaving: boolean;
  serviceName?: RailzServiceNames;
  accounts: AspChartOfAccount[];
  currentChartReceivable: string;
  currentChartPayable: string;
  originalChartPayables: string;
  originalChartReceivable: string;
  onReceivableChange: (value: string) => void;
  onPayableChange: (value: string) => void;
};

export default function ChartOfAccounts({
  onSave,
  isSaving,
  serviceName,
  accounts,
  currentChartReceivable,
  currentChartPayable,
  originalChartPayables,
  originalChartReceivable,
  onReceivableChange,
  onPayableChange,
}: ChartOfAccountsProps) {
  const hasAccounts = accounts.length > 0;

  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.AccountingSystem.AccountingSoftware.ChartOfAccount',
  });

  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });

  const accountOptions = accounts.reduce<{ [accountRefId: string]: string }>(
    (result, account) => ({ ...result, [account.AccountRefID]: account.Name || account.AccountRefID }),
    {},
  );

  return (
    <AccountingSoftwareLayout
      leftContent={
        <div className={styles.leftContainer}>
          <Box flx="col" base="8" gap="lg" jc="sa">
            <Title title={t('title', { defaultValue: 'Accounting Data Integration' })} />

            <p className={styles.paragraph}>
              {t('description', {
                defaultValue:
                  'VoPay integrates with your accounting system by allowing you to either import your A/R and A/P data into VoPay Portal or embed VoPay Portal payment rails directly within your accounting system.',
              })}
            </p>
          </Box>
        </div>
      }
      rightContent={
        <div className={styles.rightContainer}>
          <Box base="5" flx="col" gap="lg">
            <SubTitle
              subTitle={t('Form.title', { defaultValue: 'Import Your Accounting Data into Vopay' })}
              variant="small"
            />

            <Box flx="col">
              <Box flx="col">
                <h3 className={styles.label}>
                  {t('Form.stepOne', { defaultValue: 'Step 1. Connected to: {{serviceName}}', serviceName })}
                </h3>

                {serviceName && (
                  <img
                    className={styles.img}
                    src={AccountingImageMap[serviceName]?.src || ''}
                    alt={AccountingImageMap[serviceName]?.alt || ''}
                  />
                )}
              </Box>

              <Box flx="col" maxwidth="900px" pt="lg">
                <h3 className={styles.label}>
                  {t('Form.stepTwo', {
                    defaultValue: 'Step 2. Select the GL account for the bank account handling Accounts Payable',
                  })}
                </h3>

                {!hasAccounts && (
                  <div className={styles.paragraph}>
                    {t('Form.noChartOfAccountsPayables', {
                      defaultValue:
                        'Please set up at least once record in your accounting system chart of accounts in order to enable this integration.',
                    })}
                  </div>
                )}

                {hasAccounts && (
                  <Select
                    name="chartofAccountsPayable"
                    variant="outlined"
                    placeholder={t('Form.selectStepPlaceholder', { defaultValue: 'Select a GL account' })}
                    selectionAreaHeight="200px"
                    options={accountOptions}
                    value={currentChartPayable}
                    onChange={(e) => {
                      onPayableChange(e.target.value);
                    }}
                    onBlur={() => {}}
                  />
                )}
              </Box>

              <Box flx="col" maxwidth="900px" pt="lg">
                <h3 className={styles.label}>
                  {t('Form.stepThree', {
                    defaultValue: 'Step 3. Select the GL account for the bank account handling Accounts Receivable',
                  })}
                </h3>

                {!hasAccounts && (
                  <div className={styles.paragraph}>
                    {t('Form.noChartOfAccountsReceivables', {
                      defaultValue:
                        'Please set up at least once record in your accounting system chart of accounts in order to enable this integration.',
                    })}
                  </div>
                )}

                {hasAccounts && (
                  <Select
                    name="chartofAccountsReceivables"
                    variant="outlined"
                    placeholder={t('Form.selectStepPlaceholder', { defaultValue: 'Select a GL account' })}
                    selectionAreaHeight="200px"
                    options={accountOptions}
                    value={currentChartReceivable}
                    onChange={(e) => {
                      onReceivableChange(e.target.value);
                    }}
                    onBlur={() => {}}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </div>
      }
      bottomRow={
        <Box flx="row" jc="fe">
          <Button
            id="confirm-accounting-system"
            label={
              originalChartPayables === '' || originalChartReceivable === ''
                ? tGlobal('Buttons.confirm', { defaultValue: 'Confirm' })
                : tGlobal('Buttons.update', { defaultValue: 'Update' })
            }
            onClick={() => {
              onSave();
            }}
            isDisabled={
              !hasAccounts ||
              isSaving ||
              !currentChartPayable ||
              !currentChartReceivable ||
              (originalChartPayables === currentChartPayable && originalChartReceivable === currentChartReceivable)
            }
            height="36px"
          />
        </Box>
      }
    />
  );
}
